import axios from '@/plugins/axios';
import {
  deleteData, getData, postData, putData
} from '@/utils/apiTools';
import { User } from '@/models/User';
import {
  BasicDataRequest,
  CertificateListItem,
  CertificatesRequest, CompanyInformationRequest,
  EducationListItem,
  EducationsRequest,
  Experiences,
  Profile,
  ProjectPreferencesRequest,
  ReferencesRequest,
  SkillListItemRequest,
  SkillListRequest
} from '@/models/Profile';
import { ExternalFile } from '@/models/Download';
import { ProfilePdfAnalysisMode, ProfilePdfAnalysisResponse } from '@/views/profile/models/ProfileExtraction';
import BCXHTMLSanitizer from '@/utils/BCXHTMLSanitizer';

const ensureProfileApiSuccess = <T>(response: T | null, action: string): T => {
  if (response === null) {
    throw new Error(`Profile API request failed: ${action}`);
  }
  return response;
};

// eslint-disable-next-line import/prefer-default-export
export const getUser = async (userId:string):Promise<User> => {
  const user = await getData(`/v1/users/${userId}/banner`);
  user.fullname = `${user.firstname} ${user.lastname}`;
  user.userId = userId;
  return user;
};

const sortProfileItemsList = <T extends CertificateListItem | EducationListItem>(list: T[]) => {
  list.sort((a:T, b:T) => (a?.position ?? 0) - (b?.position ?? 0));
};

const sortProfileItems = (profile: Profile) => {
  if (profile?.certificates?.certificatesList?.length) sortProfileItemsList(profile.certificates.certificatesList);
  if (profile?.educations?.educationsList?.length) sortProfileItemsList(profile.educations.educationsList);
};

export const getProfile = async (userId:string):Promise<Profile> => {
  const profile = await getData(`/v1/users/${userId}/profile`);
  sortProfileItems(profile);
  return profile;
};
export const getOwnProfile = async ():Promise<Profile> => getProfile('user');

export const updateBasicData = async (data:BasicDataRequest):Promise<any> => ensureProfileApiSuccess(
  await putData('/v1/users/user/profile/basic', data),
  'updateBasicData',
);

export const updateProjectPreferences = async (data:ProjectPreferencesRequest):Promise<any> => ensureProfileApiSuccess(
  await putData('/v1/users/user/profile/projectpreferences', data),
  'updateProjectPreferences',
);

export const updateCertificates = async (data:CertificatesRequest):Promise<any> => {
  data.certificates.certificatesList = data.certificates.certificatesList.map((item, index) => ({ ...item, position: index }));
  return ensureProfileApiSuccess(
    await putData('/v1/users/user/profile/certificates', data),
    'updateCertificates',
  );
};
export const updateEducations = async (data:EducationsRequest):Promise<any> => {
  data.educations.educationsList = data.educations.educationsList.map((item, index) => ({ ...item, position: index }));
  return ensureProfileApiSuccess(
    await putData('/v1/users/user/profile/educations', data),
    'updateEducations',
  );
};

export const addReferences = async (data:ReferencesRequest):Promise<any> => ensureProfileApiSuccess(
  await putData('/v1/users/user/profile/references', data),
  'addReferences',
);

export const getReferenceDownload = async (referenceId:string):Promise<ExternalFile> => getData(
  `/v1/users/user/profile/references/${referenceId}/download`,
);

export const addSkill = async (data:SkillListItemRequest):Promise<any> => ensureProfileApiSuccess(
  await postData('/v1/users/user/profile/skills/', data),
  'addSkill',
);
export const updateSkill = async (userSkillId:string, data:SkillListItemRequest):Promise<any> => ensureProfileApiSuccess(
  await putData(`/v1/users/user/profile/skills/${userSkillId}`, data),
  'updateSkill',
);

export const updateSkills = async (data: SkillListRequest):Promise<any> => ensureProfileApiSuccess(
  await putData('/v1/users/user/profile/skills', data),
  'updateSkills',
);

export const getExperiences = async ():Promise<any> => getData('/v1/users/user/profile/experiences');

export const updateExperiences = async (data: Experiences): Promise<Experiences> => {
  data.experiencesText = BCXHTMLSanitizer.beforeSave(data.experiencesText);
  return ensureProfileApiSuccess(
    await putData('/v1/users/user/profile/experiences', data),
    'updateExperiences',
  );
};

export const deleteExperiences = async () => deleteData('/v1/users/user/profile/experiences');

export const getOwnExperienceDownload = async ():Promise<any> => getData(
  '/v1/users/user/profile/experiences/download'
);
export const getExperienceDownload = async (userId:string):Promise<any> => getData(
  `/v1/users/${userId}/profile/experiences/download`
);

export const updateCompanyInformation = async (data: CompanyInformationRequest) => putData('/v1/users/user/profile/company-information', data);

export const updateAvatar = async (
  imageData: string,
  imageType: string,
  filename = 'avatar',
): Promise<void> => {
  await axios.post('/v1/users/user/avatar', {
    filename,
    imageData,
    imageType,
  });
};

// AI extraction of a profile PDF. Multipart body: the raw PDF bytes,
// the analysis mode (CREATE / UPDATE), and the user's free-form prompt
// from the popup.
export const analyzePdf = async (
  pdfBase64: string,
  fileName: string,
  userPrompt: string,
  analysisMode: ProfilePdfAnalysisMode = 'CREATE',
  signal?: AbortSignal,
): Promise<ProfilePdfAnalysisResponse> => {
  const byteCharacters = atob(pdfBase64);
  const byteArray = new Uint8Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i += 1) {
    byteArray[i] = byteCharacters.charCodeAt(i);
  }
  const formData = new FormData();
  formData.append('pdf', new Blob([byteArray], { type: 'application/pdf' }), fileName);
  formData.append('analysisMode', analysisMode);
  formData.append('userPrompt', userPrompt);
  return (await axios.post('/v1/users/user/profile/pdf-analysis', formData, {
    headers: {
      'Content-Type': undefined,
    },
    signal,
  })).data;
};
